import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { useAdmin } from "../admin-context";
import { useAppContext } from "../../../context";
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import DataTable from "../DataTable";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmtNum = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

const StatCard = ({ icon, label, value, sub, color, positive }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
      {sub && (
        <p className={`text-xs mt-0.5 font-semibold ${positive === true ? "text-emerald-500" : positive === false ? "text-red-500" : "text-gray-400"}`}>
          {sub}
        </p>
      )}
    </div>
  </div>
);

const SectionCard = ({ title, children, subtitle }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-50">
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const STATUS_COLORS = {
  pending:   "bg-yellow-100 text-yellow-800",
  shipped:   "bg-blue-100 text-blue-800",
  delivered: "bg-green-100 text-green-800",
  confirmed: "bg-purple-100 text-purple-800",
};

// ── Orders page ───────────────────────────────────────────────
const Orders = () => {
  const { orders, updateOrderStatus, hasPermission } = useAdmin();
  const { formatPrice } = useAppContext();
  const canEdit = hasPermission("orders.edit");

  // ── Derived stats ─────────────────────────────────────────
  const totalOrders = orders.length;
  const delivered   = orders.filter(o => o.status === "delivered").length;
  const processing  = orders.filter(o => o.status === "pending" || o.status === "confirmed").length;
  const shipped     = orders.filter(o => o.status === "shipped").length;
  const deliveryPct = totalOrders > 0 ? ((delivered / totalOrders) * 100).toFixed(1) : 0;

  // ── Monthly chart data grouped from real orders ───────────
  const monthlyData = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      if (!order.date) return;
      const d = new Date(order.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!map[key]) {
        map[key] = {
          month: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
          _sort: d.getFullYear() * 100 + d.getMonth(),
          pending: 0, confirmed: 0, shipped: 0, delivered: 0,
        };
      }
      const s = order.status || "pending";
      if (map[key][s] !== undefined) map[key][s]++;
    });
    return Object.values(map).sort((a, b) => a._sort - b._sort);
  }, [orders]);

  const columns = [
    {
      key: "id",
      label: "Order ID",
      sortable: true,
      render: (val) => (
        <span className="font-mono font-bold text-gray-700">#{val}</span>
      ),
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (val) => (
        <span className="text-sm text-gray-500">
          {val ? new Date(val).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Total",
      sortable: true,
      render: (val, row) => {
        const total = val != null
          ? Number(val)
          : (row.items || []).reduce(
              (sum, item) => sum + Number(item.price) * Number(item.quantity),
              0
            );
        return <span className="font-bold text-gray-900">{formatPrice(total)}</span>;
      },
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (val) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[val] || "bg-gray-100 text-gray-800"}`}>
          {val || "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Orders</h1>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="📦" label="Total Orders" value={fmtNum(totalOrders)}  sub="All time"                         color="bg-indigo-500" />
        <StatCard icon="✓"  label="Delivered"    value={fmtNum(delivered)}    sub={`${deliveryPct}% fulfillment`}    color="bg-emerald-500" />
        <StatCard icon="⏳" label="Processing"   value={fmtNum(processing)}   sub="Pending + confirmed"              color="bg-amber-500" />
        <StatCard icon="🚚" label="Shipped"      value={fmtNum(shipped)}      sub="In transit"                       color="bg-blue-500" />
      </div>

      {/* Chart */}
      <SectionCard title="Orders by Month" subtitle="Breakdown by status">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="pending"   fill="#fbbf24" radius={[4,4,0,0]} name="Pending"   stackId="a" />
            <Bar dataKey="confirmed" fill="#a78bfa" radius={[4,4,0,0]} name="Confirmed" stackId="a" />
            <Bar dataKey="shipped"   fill="#60a5fa" radius={[4,4,0,0]} name="Shipped"   stackId="a" />
            <Bar dataKey="delivered" fill="#34d399" radius={[4,4,0,0]} name="Delivered" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={orders}
        loading={!orders.length}
        searchKeys={["id", "status"]}
        searchPlaceholder="Search orders..."
        filters={[
          {
            key: "status",
            label: "Status",
            options: [
              { label: "Pending",   value: "pending" },
              { label: "Confirmed", value: "confirmed" },
              { label: "Shipped",   value: "shipped" },
              { label: "Delivered", value: "delivered" },
            ],
          },
        ]}
        defaultPageSize={10}
        pageSizeOptions={[10, 25, 50]}
        emptyText="No orders found."
        actions={(row) => (
          <>
            {canEdit ? (
              <select
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white transition"
                value={row.status || "pending"}
                onChange={(e) => updateOrderStatus(row.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
              </select>
            ) : (
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[row.status] || "bg-gray-100 text-gray-800"}`}>
                {row.status || "—"}
              </span>
            )}
            <Link
              to={`/admin/orders/${row.id}`}
              aria-label={`View order ${row.id}`}
              className="p-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition"
            >
              <Eye size={15} />
            </Link>
          </>
        )}
      />
    </div>
  );
};

export default Orders;
